import React from 'react';
import * as PropTypes from 'prop-types';
import { MonacoEditor } from './MonacoEditor';
import { MarkdownToolbar } from './toolbar';
import { toggleInlineStyle } from 'lib/editor/MarkdownEditor/actions/toggleInlineStyle';
import { insertBlockPlaceholder } from 'lib/editor/MarkdownEditor/actions/insertBlockPlaceholder';
import { insertLink } from 'lib/editor/MarkdownEditor/actions/insertLink';
import { insertImage } from 'lib/editor/MarkdownEditor/actions/insertImage';
import { DraftViewer } from 'lib/viewer';
import { markdown2raw } from 'lib/convert';

export class MarkdownEditor extends React.Component {

	static propTypes = {
		defaultValue: PropTypes.string,
		autoFocus: PropTypes.bool,
	}

	state = {
		preview: false,
	}

	render() {
		const markdown = this.props.markdown;
		const preview = this.state.preview;
		return (
			<div
				className="draft-markdown-editor rno-markdown"
				ref={element => this.element = element}
				onPaste={evt => {
					const file = evt.clipboardData.files && evt.clipboardData.files[0];
					if (file) {
						if (/\.(png|jpe?g|gif)$/i.test(file.name) || /^image\//.test(file.type)) {
							this.handleMarkdownCommand('image', file)}
						}
					}
				}>
				<MarkdownToolbar
					onQuery={this.handleCommandQuery}
					onCommand={(command, params, evt) => {
						this.handleMarkdownCommand(command, params, evt)
					}}
				/>
				<MonacoEditor
					autoFocus={this.props.autoFocus}
					language="markdown"
					ref={editor => this.editor = editor}
					value={markdown}
					onReady={editor => this.registerMarkdownCommands(editor)}
					onChange={
						this.props.onChange ?
						() => {
							if (!this.muteChange) {
								this.props.onChange(this);
							}
						} : null
					}
					onSave={this.props.onSave}
					onFocus={this.props.onFocus}
					onBlur={this.props.onBlur}
					placeholder={this.props.placeholder}
					loadingPlaceholder="正在加载 Markdown 编辑器..."
				/>
				{ preview &&
					<DraftViewer className="markdown-preview" raw={preview} />
				}
			</div>
		);
	}

	componentDidMount() {
		if (this.props.autoLayout) {
			window.addEventListener(
				'resize',
				this._resizeHandler = () => this.layout()
			);
		}
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.markdown !== this.props.markdown) {
			this.setMarkdown(nextProps.markdown);
		}
	}

	componentWillUnmount() {
		if (this._resizeHandler) {
			window.removeEventListener('resize', this._resizeHandler);
		}
	}

	/**
	 * @param {monaco.editor.IStandaloneCodeEditor} editor
	 */
	registerMarkdownCommands(editor) {
		this.monacoEditor = editor;
		editor.addAction({
			id: 'bold',
			keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KEY_B],
			label: '设置加粗样式',
			run: editor => toggleInlineStyle(editor, '**', '加粗')
		});
		editor.addAction({
			id: 'italic',
			keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KEY_I],
			label: '设置斜体样式',
			run: editor => toggleInlineStyle(editor, '*', '斜体')
		});
		editor.addAction({
			id: 'strike',
			keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KEY_D],
			label: '设置删除线样式',
			run: editor => toggleInlineStyle(editor, '~~', '删除线')
		});
		editor.addAction({
			id: 'code',
			label: '设置代码样式',
			run: editor => toggleInlineStyle(editor, '`', '代码')
		});
		editor.addAction({
			id: 'h1',
			label: '插入一级标题',
			run: editor => insertBlockPlaceholder(editor, '# '),
		});
		editor.addAction({
			id: 'h2',
			label: '插入二级标题',
			run: editor => insertBlockPlaceholder(editor, '## '),
		});
		editor.addAction({
			id: 'h3',
			label: '插入三级标题',
			run: editor => insertBlockPlaceholder(editor, '### '),
		});
		editor.addAction({
			id: 'h4',
			label: '插入四级标题',
			run: editor => insertBlockPlaceholder(editor, '#### '),
		});
		editor.addAction({
			id: 'quote',
			label: '插入引用内容',
			run: editor => insertBlockPlaceholder(editor, '> '),
		});
		editor.addAction({
			id: 'ol',
			label: '插入编号列表',
			run: editor => insertBlockPlaceholder(editor, '1. '),
		});
		editor.addAction({
			id: 'ul',
			label: '插入列表',
			run: editor => insertBlockPlaceholder(editor, '* '),
		});
		editor.addAction({
			id: 'code-block',
			label: '插入代码块',
			run: editor => insertBlockPlaceholder(editor, '```js\n// 输入代码内容\n```\n')
		});
		editor.addAction({
			id: 'table',
			label: '插入表格',
			run: editor => insertBlockPlaceholder(editor,
				'表头 1 | 表头 2 | 表头 3\n' +
				'------|--------|------\n' +
				'内容 1 | 内容 2 | 内容 3\n'
			)
		});
		editor.addAction({
			id: 'formula',
			label: '插入公式',
			run: editor => {
				const selection = editor.getSelection();
				if (selection.startLineNumber === selection.endLineNumber
					&& editor.getModel().getLineContent(selection.startLineNumber)
				) {
					toggleInlineStyle(editor, '`$', 'x^2 + y^2 = r^2');
				} else {
					insertBlockPlaceholder(editor, '> `$ x^2 + y^2 = r^2 $`');
				}
			}
		});
	}

	async handleMarkdownCommand(command, params, evt) {
		switch (command) {
			case 'bold':
			case 'italic':
			case 'strike':
			case 'code':
			case 'h1':
			case 'h2':
			case 'h3':
			case 'h4':
			case 'quote':
			case 'code-block':
			case 'ul':
			case 'ol':
			case 'table':
			case 'formula':
				const action = this.monacoEditor.getAction(command);
				if (action) {
					await action.run();
					this.monacoEditor.focus();
				}
				break;
			case 'link': {
				const url = params;
				insertLink(this.monacoEditor, url);
				this.monacoEditor.focus();
				break;
			}
			case 'image': {
				const file = params;
				insertImage(this.monacoEditor, file, {
					imageSizeLimit: this.props.imageSizeLimit,
					uploadImage: this.props.handleImageUpload,
				});
				break;
			}
			case 'word':
				break;
			case 'preview':
				this.setState({
					preview: this.state.preview ? null : markdown2raw(this.editor.getValue())
				});
				break;
		}
	}

	handleCommandQuery = (command) => {
		if (command === 'preview') {
			return {
				actived: !!this.state.preview,
				disabled: false,
			};
		}
		return {
			actived: false,
			disabled: false,
		};
	}

	getElement() {
		return this.element;
	}

	getMonacoEditor() {
		return this.editor;
	}

	getMarkdown() {
		if (this.editor) {
			return this.editor.getValue()
		}
		throw new Error('Monaco editor is not mounted yet!');
	}

	setMarkdown(value) {
		if (this.editor) {
			this.muteChange = true;
			this.editor.setValue(value);
			this.muteChange = false;
			return;
		}
		throw new Error('Monaco editor is not mounted yet!');
	}

	layout() {
		// layout for height change
		if (this.editor) {
			this.editor.layout();
		}
	}
}



// WEBPACK FOOTER //
// ./lib/editor/MarkdownEditor/MarkdownEditor.jsx